// 1:1 port of frontend/src/utils/parseAiSettings.ts
const SETTINGS_KEYS = new Set([
  "laser_model", "substrate_detail", "substrate_category", "contaminant", "pattern", "power_percent", "frequency_khz",
  "pulse_width_ns", "speed_mm_s", "pattern_size_mm", "hatch_1_mm", "hatch_2_mm", "metal_thickness_mm", "focal_distance_mm", "passes", "notes",
]);

function looksLikeSettings(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  let hits = 0;
  for (const k of Object.keys(obj)) {
    if (SETTINGS_KEYS.has(k)) hits++;
    if (hits >= 2) return true;
  }
  return false;
}

function tryParse(text) {
  try {
    const obj = JSON.parse(text);
    return looksLikeSettings(obj) ? obj : null;
  } catch {
    return null;
  }
}

const FENCE_RE = /```([a-zA-Z_-]+)?\s*\n?([\s\S]*?)```/g;

function extractBareJsonBlock(text, startIdx) {
  let i = text.indexOf("{", startIdx);
  if (i < 0) return null;
  const lineStart = text.lastIndexOf("\n", i);
  if (lineStart !== -1 && text.slice(lineStart + 1, i).trim().length > 0) return null;
  let depth = 0, inString = false, escape = false;
  for (; i < text.length; i++) {
    const ch = text[i];
    if (escape) { escape = false; continue; }
    if (ch === "\\") { escape = true; continue; }
    if (ch === "\"") { inString = !inString; continue; }
    if (inString) continue;
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) return { end: i + 1, body: text.slice(text.indexOf("{", startIdx), i + 1) };
    }
  }
  return null;
}

export function parseAiContent(raw) {
  if (!raw) return [];
  const segments = [];
  let cursor = 0;
  const ranges = [];
  FENCE_RE.lastIndex = 0;
  let m;
  while ((m = FENCE_RE.exec(raw)) !== null) {
    const tag = (m[1] || "").toLowerCase().trim();
    const body = (m[2] || "").trim().replace(/^\uFEFF/, "");
    const cleanedBody = body.replace(/^json\s*\n/i, "");
    const isExplicit = tag === "alr-settings" || tag === "alr_settings";
    const settings = (isExplicit || tag === "json" || tag === "" || tag === "javascript" || tag === "js") ? tryParse(cleanedBody) : null;
    ranges.push({ start: m.index, end: m.index + m[0].length, settings, raw: m[0] });
  }
  const occupied = ranges.map((r) => [r.start, r.end]);
  const isInsideFence = (idx) => occupied.some(([s, e]) => idx >= s && idx < e);
  let scanFrom = 0;
  while (true) {
    const obj = extractBareJsonBlock(raw, scanFrom);
    if (!obj) break;
    const startIdx = raw.indexOf("{", scanFrom);
    if (startIdx < 0) break;
    if (isInsideFence(startIdx)) { scanFrom = obj.end; continue; }
    const parsed = tryParse(obj.body);
    if (parsed) ranges.push({ start: startIdx, end: obj.end, settings: parsed, raw: obj.body });
    scanFrom = obj.end;
  }
  ranges.sort((a, b) => a.start - b.start);
  for (const r of ranges) {
    if (r.start > cursor) {
      const txt = raw.slice(cursor, r.start).trim();
      if (txt.length > 0) segments.push({ kind: "text", text: txt });
    }
    segments.push(r.settings ? { kind: "settings", data: r.settings } : { kind: "text", text: r.raw });
    cursor = r.end;
  }
  if (cursor < raw.length) {
    const tail = raw.slice(cursor).trim();
    if (tail.length > 0) segments.push({ kind: "text", text: tail });
  }
  if (segments.length === 0) return [{ kind: "text", text: raw }];
  return segments;
}
